import { useState, useCallback } from 'react';
import { addDays, startOfWeek, endOfWeek, format } from 'date-fns';
import { getToday, getWeekStart } from '../utils/timeUtils';

/**
 * カレンダーの状態を管理するカスタムフック
 */
export const useCalendar = () => {
  const [currentDate, setCurrentDate] = useState(getToday());
  const [viewMode, setViewMode] = useState('workweek'); // 'workweek'（月〜金）or 'fullweek'（日〜土）

  /**
   * 今日に移動
   */
  const goToToday = useCallback(() => {
    setCurrentDate(getToday());
  }, []);

  /**
   * 前の日/週に移動
   */
  const goToPrevious = useCallback(() => {
    if (viewMode === 'day') {
      setCurrentDate(prev => addDays(prev, -1));
    } else {
      setCurrentDate(prev => addDays(prev, -7));
    }
  }, [viewMode]);

  /**
   * 次の日/週に移動
   */
  const goToNext = useCallback(() => {
    if (viewMode === 'day') {
      setCurrentDate(prev => addDays(prev, 1));
    } else {
      setCurrentDate(prev => addDays(prev, 7));
    }
  }, [viewMode]);

  /**
   * 特定の日付に移動
   */
  const goToDate = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  /**
   * 表示モードを切り替え
   */
  const toggleViewMode = useCallback(() => {
    setViewMode(prev => (prev === 'day' ? 'week' : 'day'));
  }, []);

  /**
   * 表示モードを設定
   */
  const setView = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  /**
   * 現在の日付範囲を取得
   */
  const getDateRange = useCallback(() => {
    if (viewMode === 'day') {
      return {
        start: currentDate,
        end: currentDate,
      };
    } else {
      const weekStart = getWeekStart(currentDate);
      return {
        start: weekStart,
        end: addDays(weekStart, 6),
      };
    }
  }, [currentDate, viewMode]);

  /**
   * 表示用のタイトルを取得
   */
  const getTitle = useCallback(() => {
    if (viewMode === 'day') {
      return format(currentDate, 'yyyy年MM月dd日 (E)');
    } else {
      const range = getDateRange();
      return `${format(range.start, 'yyyy年MM月dd日')} - ${format(range.end, 'MM月dd日')}`;
    }
  }, [currentDate, viewMode, getDateRange]);

  /**
   * 週の日付配列を取得
   */
  const getWeekDates = useCallback(() => {
    const weekStart = getWeekStart(currentDate); // 日曜日
    if (viewMode === 'workweek') {
      // 稼働日（月〜金）のみ返す（weekStartは日曜なので1〜5番目）
      return Array.from({ length: 5 }, (_, i) => addDays(weekStart, i + 1));
    } else {
      // 全週（日〜土）を返す
      return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    }
  }, [currentDate, viewMode]);

  return {
    currentDate,
    viewMode,
    goToToday,
    goToPrevious,
    goToNext,
    goToDate,
    toggleViewMode,
    setView,
    getDateRange,
    getTitle,
    getWeekDates,
  };
};

export default useCalendar;
